import { MeasuringCategory, ProfileInterface, UserProfileRole } from 'ts-interfaces'
import { create } from 'zustand'
import { firebaseDB, profilesDB } from '../../libs/firebase'
import { produce } from 'immer'
import { doc, getDoc, getDocs, query, where, collection } from 'firebase/firestore'
import { getUserLocationFromIP,generateID, getUserLocationFromIPdata } from "../helper";
import { updateProfile } from '../../libs/util'
import { firebaseHelper, usePermissions, useDesignFirm } from 'ui'
import { Permissions } from "ts-interfaces";


export interface LoginData {
  user: any
  providerId?: string
  _tokenResponse?: any
}

export interface LoginState {
  profile: ProfileInterface | null
  showLogin: any
  
  // Teams PRO access state
  isProUser: boolean
  isTeamsProUser: boolean
}


let globalLoginStateIsProcessing=false; 


export const useLoginState = create( (set:any, get:any) => ({
  profile:null,
  showLogin:null,
  processing:false,
  firebaseDoc:null,
  permissions: null,
  
  // Teams PRO access state
  isProUser: false,
  isTeamsProUser: false,



  getPermmissions: async (skipTeamsProUpdate = false): Promise<Permissions | null> => {
    if (get().permissions) return get().permissions 
    const uid = get().profile?.uid 
    if (get().profile?.uid == null) return null;
    const docRef = doc(firebaseHelper.db, 'permissions', uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as Permissions;
      set({permissions:data})
      // Update Teams PRO access when permissions are loaded (unless we're already updating it)
      if (!skipTeamsProUpdate) {
        get().updateTeamsProAccess()
      }
      return data
    }
    else { return null; }
  },



  // Update Teams PRO access status
  updateTeamsProAccess: async () => {
    const state = get();
    const profile = state.profile;
    const currentUserUid = profile?.uid;
    
    if (!currentUserUid || !profile) {
      set({
        isProUser: false,
        isTeamsProUser: false,
      });
      return;
    }

    try {
      // Check if user is PRO
      const isProUser = profile && (profile as any)?.userRole === UserProfileRole.PRO;

      // Get permissions if not already loaded
      let permissions = state.permissions;
      if (!permissions) {
        permissions = await get().getPermmissions(true); // Skip teams pro update to prevent infinite loop
      }
      
      // Get design firm info and check Teams PRO status
      let designFirm: any = null;
      let isTeamsProUser = false;
      
      if (permissions?.teamsDesigner?.[0]) {
        // This is a simplified version - in real implementation this would use the useDesignFirm hook
        // For now, we'll just check if they have teamsDesigner permission
        designFirm = { firm: { name: 'Design Firm' } }; // Placeholder for design firm info
        
        // Query the teams collection to find if user is part of an active team
        try {
          const teamsCollection = collection(firebaseHelper.db, 'teams');
          const teamQuery = query(teamsCollection, where("uid", "==", currentUserUid));
          const teamSnapshot = await getDocs(teamQuery);
          
          // Check if any team document has active status
          for (const teamDoc of teamSnapshot.docs) {
            const teamData = teamDoc.data();
            if (teamData && teamData.status === 'active') {
              isTeamsProUser = true;
              break; // Found an active team, no need to check further
            }
          }
        } catch (error) {
          console.error('Error checking team status:', error);
        }
      }
      
      // Final Teams PRO determination: must be PRO user AND have design firm access
      isTeamsProUser = isProUser && isTeamsProUser && Boolean(designFirm?.firm?.name);

      set({
        isProUser,
        isTeamsProUser
      });
      
    } catch (error) {
      console.error('Error updating Teams PRO access:', error);
      set({
        isProUser: false,
        isTeamsProUser: false,
      });
    }
  },

  setShowLogin: (showLogin: any)=> set({showLogin}),
  setProfile: (profile:any) => {    
    set({processing:false})
    if (!profile || !profile?.id) { 
      set({
        firebaseDoc:null,
        permissions:null, 
        profile:null, 
        processing:false, 
        isProUser: false,
        isTeamsProUser: false,
      })
    }
    else {       
      set({profile:profile})
      // Update Teams PRO access when profile is set
      get().updateTeamsProAccess()
    }
  },
  setIncrementRenderCount: async () => {
    if (globalLoginStateIsProcessing==true) return;
    const profile = get().profile
    if (!profile) return;
    try {
      globalLoginStateIsProcessing=true;
      const newProfile:ProfileInterface |any = produce(profile, (draft:ProfileInterface) => {
        draft.renders = draft.renders ? draft.renders + 1 : 1
      })
      await get().updateAndSaveProfile(newProfile)
    }
    finally { globalLoginStateIsProcessing=false; }
  },  
  //replaces the createProfileIfNotExists function
  //only useful when we have loginData
  //most important thing.. if i end up createing a profile 
  //Only one scenario where this is a problem -> user signups, then logs out, then logs in really fast. 
  setOrCreateProfile: async (loginData:LoginData, mailingList=false) => {
    if (get().profile?.id || get().processing==true) return;
    if (globalLoginStateIsProcessing==true) return;
    set({processing:true})
    try {
      globalLoginStateIsProcessing = true
      const resp = await createProfileIfNotExists(loginData, mailingList)
      if (resp?.profile?.id) { 
        set({ firebaseDoc:resp.ref}) 
        get().setProfile(resp.profile) // Use setProfile method to trigger updateTeamsProAccess
      }
      else { 
        get().setProfile(null) // Use setProfile method to clear state properly
      }
      return resp;
    }
    finally { set({processing:false}); globalLoginStateIsProcessing=false; }
  },

  getUserProfile: async (loginData:LoginData) => {
    if (get().profile?.id || get().processing==true) return;
    if (globalLoginStateIsProcessing==true) return;
    set({processing:true})
    try {
      globalLoginStateIsProcessing = true
      const resp = await getProfile(loginData)
      if (resp?.profile?.id) { 
        set({ firebaseDoc:resp.ref}) 
        get().setProfile(resp.profile) // Use setProfile method to trigger updateTeamsProAccess
      }
      else { 
        get().setProfile(null) // Use setProfile method to clear state properly
      }
      return resp;
    }
    finally { set({processing:false}); globalLoginStateIsProcessing=false; }
  },

  updateAndSaveProfile: async (profile:ProfileInterface, pic={}) => {
    return new Promise((resolve, reject) => {
      if (get().profile?.uid == null) reject("User not logged in")
      if (!get().firebaseDoc) reject("No profile reference found")
      if (get().processing==true) reject("Processing") 
      updateProfile(profile,pic, get().firebaseDoc as any).then(result=>{
        set({profile:result})
        resolve(result)
      }).catch(error=> { console.log("error ", error); reject(error) })
    })
  },







}))





let globalProfileIsProcessing=false; 
const  createProfileIfNotExists = async (firebaseData,mailingList)=>   {
  try {
    console.log("Visiting craeteProfile if not exists");
    if(globalProfileIsProcessing==true) { return;  }
    globalProfileIsProcessing = true;
    if ( (!firebaseData?.user?.uid) || (!firebaseData?.user.email)) return null;   
    const uid = firebaseData.user.uid;
    const email = firebaseData.user.email;
    if (profilesDB==null) throw new Error("profilesDB is not defined")
    const q = query(profilesDB, where("uid", "==", uid));
    const querySnapshot = await getDocs(q);    
    if(querySnapshot.metadata.fromCache) {
      globalProfileIsProcessing = false;
      return;
    }
    if (querySnapshot.docs.length > 0) { 
      const profile = querySnapshot.docs[0].data() as ProfileInterface
      if(!profile?.country) {
        const newProfile: ProfileInterface = await getUserCountry(querySnapshot);        
        return {ref:querySnapshot.docs[0].ref, updated:false, profile:newProfile} 
      }
      return {ref:querySnapshot.docs[0].ref, updated:false, profile:profile} 
    }
    else {       
      console.log("Now I am creating a new profile record");
      let country:any = 'United States'
      try {       
        const resp:any= await getUserLocationFromIPdata(2000);  
        country = resp?.country_name || 'United States'
      }
      catch (err) { console.log('Could not get user location'); }      
      const isGoogle = firebaseData.providerId=='google.com'
      const first_name = isGoogle ? firebaseData?._tokenResponse?.firstName : ''; 
      const last_name = isGoogle ? firebaseData?._tokenResponse?.lastName : '';  
      if (!uid || !email ) { throw new Error("uid and email are required"); }
      if (uid?.length < 5 || email?.length < 3) { throw new Error("uid and email are required"); }  
  
      const measuringCategoryValue = country == 'United States'?  MeasuringCategory.IMPERIAL: MeasuringCategory.METRIC
      const profile:ProfileInterface = {id:generateID(), uid:uid, email:email,country:country,
        measuring_category:measuringCategoryValue,
        measuringCategory:measuringCategoryValue,
        renders:0,
        mailingList:mailingList,createdAt:new Date(), created_at:new Date()}
      if(first_name?.length > 1) profile['first_name'] = first_name;
      if(last_name?.length > 1) profile['last_name'] = last_name;
        //create ref here. 
      const firebaseRef = doc(firebaseDB as any, 'profiles', profile.id as string)
      const result = await updateProfile(profile,{},firebaseRef)
      return {ref:firebaseRef,  updated:true, profile:result as ProfileInterface}
      }
    }
    catch (e) { console.log(e) } 
    finally { 
      globalProfileIsProcessing = false;
    }
}

const getProfile = async (firebaseData) => {
  try {
    if (globalProfileIsProcessing==true) { return;  }
    globalProfileIsProcessing = true;
    if ( (!firebaseData?.user?.uid) || (!firebaseData?.user.email)) return null;   
    const uid = firebaseData.user.uid;
    if (profilesDB==null) throw new Error("profilesDB is not defined")
    const q = query(profilesDB, where("uid", "==", uid));
    const querySnapshot = await getDocs(q);    
    // console.log('how many times here?');
    if (querySnapshot.docs.length > 0) { 
      const profile = querySnapshot.docs[0].data() as ProfileInterface
      if(!profile?.country) {
        const newProfile: ProfileInterface = await getUserCountry(querySnapshot);        
        return {ref:querySnapshot.docs[0].ref, updated:false, profile:newProfile} 
      }
      return {ref:querySnapshot.docs[0].ref, updated:false, profile:querySnapshot.docs[0].data() as ProfileInterface} 
    }
    else return null;
  }
  finally {
    globalProfileIsProcessing = false;
  }
}

const getUserCountry = async (querySnapshot: any) => {
  const profile = querySnapshot.docs[0].data() as ProfileInterface;
  let country:any = 'United States'
  try {       
    const resp:any= await getUserLocationFromIPdata(2000);  
    country = resp?.country_name || 'United States'
    const newProfile:ProfileInterface = produce(profile, (draft:ProfileInterface) => {
      draft.country = country
    })
    await updateProfile(newProfile,{},querySnapshot.docs[0].ref)
    return newProfile;
  } catch (err) { console.log('Could not get user location'); }
  return profile;
}